/**
 * Signup Page for AdCanvas
 *
 * Only handles invited users (from email link) - they need to set a password.
 * Self-signup is disabled. Non-invited users are redirected to login.
 *
 * Handles both:
 * - Hash-based flow: #type=invite or #type=signup (implicit flow)
 * - PKCE flow: ?code=xxx (modern Supabase)
 *
 * Organization assignment is handled by OrganizationContext after login.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Mail, Lock, ArrowLeft } from 'lucide-react';

export default function SignupPage() {
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingInvite, setIsCheckingInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);

  // Single useEffect to handle invite detection (eliminates race condition)
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    // Capture URL params IMMEDIATELY (before Supabase might clean them)
    const hash = window.location.hash;
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    // Determine if this LOOKS like an invite link (hash-based or PKCE)
    // NOTE: type=recovery is NOT handled here - that goes to ResetPasswordPage
    const isHashInvite = hash.includes('type=invite') || hash.includes('type=signup');
    const isPKCEInvite = !!code && window.location.pathname.includes('/signup');
    const looksLikeInvite = isHashInvite || isPKCEInvite;

    console.log('SignupPage: Checking invite', { hash, code, isHashInvite, isPKCEInvite, looksLikeInvite });

    // If no invite indicators, redirect immediately
    if (!looksLikeInvite) {
      navigate('/login');
      return;
    }

    // Handle PKCE flow: exchange code for session
    const handlePKCE = async (): Promise<boolean> => {
      if (!code || !isMounted) return false;

      try {
        console.log('SignupPage: Exchanging PKCE code for session...');
        const { data, error: pkceError } = await supabase.auth.exchangeCodeForSession(code);
        if (pkceError) {
          console.error('SignupPage: PKCE exchange error:', pkceError);
          return false;
        }
        if (data.session?.user?.email && isMounted) {
          console.log('SignupPage: PKCE exchange successful');
          setIsInvitedUser(true);
          setInviteEmail(data.session.user.email);
          setIsCheckingInvite(false);
          // Clean URL after successful processing
          window.history.replaceState({}, '', window.location.pathname);
          return true;
        }
      } catch (err) {
        console.error('SignupPage: PKCE error:', err);
      }
      return false;
    };

    // Listen for auth state changes (for hash-based flow)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      // Only act on relevant events
      if (event !== 'SIGNED_IN' && event !== 'USER_UPDATED') return;

      console.log('SignupPage: Auth event received:', event, session?.user?.email);

      if (session?.user?.email) {
        setIsInvitedUser(true);
        setInviteEmail(session.user.email);
        setIsCheckingInvite(false);
        clearTimeout(timeoutId);
        // Clean URL after successful processing
        window.history.replaceState({}, '', window.location.pathname);
      }
    });

    const init = async () => {
      // Try PKCE first
      if (isPKCEInvite) {
        const success = await handlePKCE();
        if (success) return;
      }

      // For hash-based flow, check if session already exists
      // (Supabase may have processed the hash by now)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email && isMounted && isHashInvite) {
        console.log('SignupPage: Found existing session');
        setIsInvitedUser(true);
        setInviteEmail(session.user.email);
        setIsCheckingInvite(false);
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // If hash-based flow, wait for onAuthStateChange (with timeout)
      if (isHashInvite) {
        console.log('SignupPage: Waiting for auth state change with 10s timeout...');
        // Give Supabase time to process the hash
        timeoutId = setTimeout(() => {
          if (isMounted) {
            // Check one more time before showing error
            supabase.auth.getSession().then(({ data: { session: finalSession } }) => {
              if (finalSession?.user?.email && isMounted) {
                setIsInvitedUser(true);
                setInviteEmail(finalSession.user.email);
              } else if (isMounted) {
                console.error('SignupPage: Timeout waiting for invite session');
                setError('Your invite link has expired or is invalid. Please contact your administrator for a new invitation.');
              }
              if (isMounted) setIsCheckingInvite(false);
            });
          }
        }, 10000); // 10 second timeout
      } else if (isMounted) {
        // PKCE failed and no hash - shouldn't happen but handle gracefully
        setError('Unable to verify your invitation. Please try clicking the link again.');
        setIsCheckingInvite(false);
      }
    };

    init();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Verify we still have a valid session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Your session has expired. Please use the invite link again or request a password reset.');
        setIsLoading(false);
        return;
      }

      // Update the user's password (they're already authenticated via invite token)
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        setError(updateError.message);
        setIsLoading(false);
        return;
      }

      // Redirect to the app - OrganizationContext will handle org assignment
      navigate('/');
    } catch (err) {
      console.error('Signup error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  // Show loading while checking for invite
  if (isCheckingInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Verifying your invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If error occurred during invite check, show error page
  if (error && !isInvitedUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              Invitation Error
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Link to="/login" className="w-full">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go to Login
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // If not invited user and no error, show nothing (shouldn't happen, redirect should have fired)
  if (!isInvitedUser || !inviteEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Invited user flow - only show password fields
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Set your password
          </CardTitle>
          <CardDescription className="text-center">
            Welcome! Set a password to complete your account setup.
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Show email as read-only */}
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{inviteEmail}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  disabled={isLoading}
                  minLength={8}
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
          </CardContent>

          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up account...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
